import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import Api from "../../BaseUrlConfigration/ApiConfig";
import { constructQueryString } from "../../Util/HelperFunction";

const getOrganisationDashboardQuery = (reqBody = {}, includeSortBy = false) => {
  const queryParams = {
    type: reqBody?.type,
    startDate: reqBody?.startDate,
    endDate: reqBody?.endDate,
    year: reqBody?.year,
    group: reqBody?.group,
    schoolId: reqBody?.schoolId,
    ...(includeSortBy ? { sortBy: reqBody?.sortBy } : {}),
  };

  return constructQueryString(queryParams);
};

const buildDashboardUrl = (path, query) => (query ? `${path}?${query}` : path);

export const resetOrganisationAction = createAction("organisation/reset");

export const organisationInvitesListAction = createAsyncThunk(
  "organisation/invitesList",
  async ({ page = 0, limit = 10, search = "" } = {}, thunkAPI) => {
    try {
      const response = await Api.get(
        `school/organisation-group/invites?page=${page}&limit=${limit}&search=${search}`
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationGroupsListAction = createAsyncThunk(
  "organisation/groupsList",
  async ({ groupId, page = 0, limit = 10, search = "", status = "", sortBy = "", sortOrder = "" }, thunkAPI) => {
    try {
      let url = `school/organisation-group/${groupId}/organisations?page=${page}&limit=${limit}&search=${search}`;
      if (status) {
        url += `&status=${status}`;
      }
      if (sortBy) {
        url += `&sortBy=${sortBy}&sortOrder=${sortOrder || "asc"}`;
      }
      const response = await Api.get(url);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationRemoveOrganisationAction = createAsyncThunk(
  "organisation/removeOrganisation",
  async ({ schoolId }, thunkAPI) => {
    try {
      const response = await Api.delete(
        `school/organisation-group/leave`,
        { data: { schoolId } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationSendInviteAction = createAsyncThunk(
  "organisation/sendInvite",
  async ({ groupId, email, inviteType, schoolId }, thunkAPI) => {
    try {
      const response = await Api.post(
        `school/organisation-group/${groupId}/send-invite`,
        { email, inviteType, schoolId }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationEmailCheckAction = createAsyncThunk(
  "organisation/emailCheck",
  async (reqBody, thunkAPI) => {
    try {
      const response = await Api.post("school/organisation-group/email-check", reqBody);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationInviteDecisionAction = createAsyncThunk(
  "organisation/inviteDecision",
  async ({ inviteId, inviteStatus }, thunkAPI) => {
    try {
      const response = await Api.post(
        `school/organisation-group/accept/${inviteId}`,
        { inviteStatus, actionType: inviteStatus }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationMembersListAction = createAsyncThunk(
  "organisation/membersList",
  async ({ page = 0, limit = 10, search = "", role = "", status = "", sortBy = "", sortOrder = "" } = {}, thunkAPI) => {
    try {
      let url = `school/organisation-group/member/list?page=${page}&limit=${limit}&search=${search}`;
      if (role) {
        url += `&role=${role}`;
      }
      if (status) {
        url += `&status=${status}`;
      }
      if (sortBy) {
        url += `&sortBy=${sortBy}&sortOrder=${sortOrder || "asc"}`;
      }
      const response = await Api.get(url);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationMemberDetailsAction = createAsyncThunk(
  "organisation/memberDetails",
  async (memberId, thunkAPI) => {
    try {
      const response = await Api.get(
        `school/organisation-group/member/details/${memberId}`
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationCreateMemberAction = createAsyncThunk(
  "organisation/createMember",
  async (reqBody, thunkAPI) => {
    try {
      const response = await Api.post(
        "school/organisation-group/member/add",
        reqBody
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationUpdateMemberAction = createAsyncThunk(
  "organisation/updateMember",
  async ({ memberId, ...reqBody }, thunkAPI) => {
    try {
      const response = await Api.put(
        `school/organisation-group/member/edit/${memberId}`,
        reqBody
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDeleteMemberAction = createAsyncThunk(
  "organisation/deleteMember",
  async (memberId, thunkAPI) => {
    try {
      const response = await Api.delete(
        `school/organisation-group/member/delete/${memberId}`
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationStaffListAction = createAsyncThunk(
  "organisation/staffList",
  async ({ page = 0, limit = 10, search = "", schoolId = "", accountType = "", ogMemberRole = "", status = "", sortBy = "", sortOrder = "" } = {}, thunkAPI) => {
    try {
      const queryParams = {
        page,
        limit,
        search,
        schoolId,
      };
      if (accountType) {
        queryParams.accountType = accountType;
      }
      if (ogMemberRole) {
        queryParams.ogMemberRole = ogMemberRole;
      }
      if (status) {
        queryParams.status = status;
      }
      if (sortBy) {
        queryParams.sortBy = sortBy;
        queryParams.sortOrder = sortOrder || "asc";
      }
      const query = constructQueryString(queryParams);
      const response = await Api.get(
        buildDashboardUrl("school/organisation-group/staff", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardUserStatsAction = createAsyncThunk(
  "organisation/dashboardUserStats",
  async (reqBody, thunkAPI) => {
    try {
      const query = getOrganisationDashboardQuery(reqBody);
      const response = await Api.get(
        buildDashboardUrl(
          "/school/organisation-group/dashboard/user-stats",
          query
        )
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardActivityAction = createAsyncThunk(
  "organisation/dashboardActivity",
  async (reqBody, thunkAPI) => {
    try {
      const query = getOrganisationDashboardQuery(reqBody);
      const response = await Api.get(
        buildDashboardUrl("/school/organisation-group/dashboard/activity", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardCategoryAction = createAsyncThunk(
  "organisation/dashboardCategory",
  async (reqBody, thunkAPI) => {
    try {
      const query = getOrganisationDashboardQuery(reqBody);
      const response = await Api.get(
        buildDashboardUrl(
          "/school/organisation-group/dashboard/category-view",
          query
        )
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardVideoAction = createAsyncThunk(
  "organisation/dashboardVideo",
  async (reqBody, thunkAPI) => {
    try {
      const query = getOrganisationDashboardQuery(reqBody, true);
      const response = await Api.get(
        buildDashboardUrl(
          "/school/organisation-group/dashboard/video-view",
          query
        )
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardTrendingTagsAction = createAsyncThunk(
  "organisation/dashboardTrendingTags",
  async (reqBody, thunkAPI) => {
    try {
      const query = getOrganisationDashboardQuery(reqBody, true);
      const response = await Api.get(
        buildDashboardUrl(
          "/school/organisation-group/dashboard/trending-tags",
          query
        )
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardRecentClassesAction = createAsyncThunk(
  "organisation/dashboardRecentClasses",
  async ({ startDate, endDate, sortBy, sortOrder, limit, schoolId } = {}, thunkAPI) => {
    try {
      const query = constructQueryString({
        startDate,
        endDate,
        sortBy,
        sortOrder,
        limit,
        schoolId,
      });
      const response = await Api.get(
        buildDashboardUrl("school/organisation-group/staff-panel/recent-classes", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardRecentCollectionsAction = createAsyncThunk(
  "organisation/dashboardRecentCollections",
  async (payload, thunkAPI) => {
    try {
      const { startDate, endDate, sortBy, sortOrder, page, limit, schoolId } = payload;
      const query = constructQueryString({
        startDate,
        endDate,
        sortBy,
        sortOrder,
        page,
        limit,
        schoolId,
      });
      const response = await Api.get(
        buildDashboardUrl("school/organisation-group/staff-panel/recent-collections", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardRecentActiveStaffAction = createAsyncThunk(
  "organisation/dashboardRecentActiveStaff",
  async (payload, thunkAPI) => {
    try {
      const { startDate, endDate, sortOrder, schoolId } = payload;
      const query = constructQueryString({
        startDate,
        endDate,
        sortOrder,
        schoolId,
      });
      const response = await Api.get(
        buildDashboardUrl("school/organisation-group/staff-panel/recent-active-staff", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);
export const organisationListAction = createAsyncThunk(
  "organisation/list",
  async ({ page = 0, limit = 10, search = "" } = {}, thunkAPI) => {
    try {
      const response = await Api.get(
        `school/organisation-group/organisations/list?page=${page}&limit=${limit}&search=${search}`
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardStaffStatsAction = createAsyncThunk(
  "organisation/dashboardStaffStats",
  async ({ startDate, endDate, schoolId } = {}, thunkAPI) => {
    try {
      const query = constructQueryString({
        startDate,
        endDate,
        schoolId,
      });
      const response = await Api.get(
        buildDashboardUrl("school/organisation-group/staff-panel/staff-stats", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardCategoryPerSchoolAction = createAsyncThunk(
  "organisation/dashboardCategoryPerSchool",
  async ({ startDate, endDate, schoolId } = {}, thunkAPI) => {
    try {
      const query = constructQueryString({
        startDate,
        endDate,
        schoolId,
      });
      const response = await Api.get(
        buildDashboardUrl("school/organisation-group/dashboard/category-per-school", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationDashboardUsageHoursAction = createAsyncThunk(
  "organisation/dashboardUsageHours",
  async ({ startDate, endDate, schoolId } = {}, thunkAPI) => {
    try {
      const query = constructQueryString({
        startDate,
        endDate,
        schoolId,
      });
      const response = await Api.get(
        buildDashboardUrl("school/organisation-group/dashboard/usage-hours", query)
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationLeaveAction = createAsyncThunk(
  "organisation/leave",
  async (_, thunkAPI) => {
    try {
      const response = await Api.delete("school/organisation-group/leave");
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationUpdatePasswordAction = createAsyncThunk(
  "organisation/updatePassword",
  async (reqBody, thunkAPI) => {
    try {
      const response = await Api.patch(
        "school/organisation-group/member/update-password",
        reqBody
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationAssignRoleAction = createAsyncThunk(
  "organisation/assignRole",
  async (payload, thunkAPI) => {
    try {
      const { staffId, role, memberType } = payload;
      const reqBody = { role, memberType };
      const response = await Api.patch(
        `school/organisation-group/staff/${staffId}/assign-role`,
        reqBody
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const organisationUpdateRoleAction = createAsyncThunk(
  "organisation/updateRole",
  async (payload, thunkAPI) => {
    try {
      const { staffId, role, memberType } = payload;
      const reqBody = { role, memberType };
      const response = await Api.patch(
        `school/organisation-group/staff/${staffId}/update-role`,
        reqBody
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);
